package com.evoapps.evoshift.domain.privacy

import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.RosterPrivacy
import com.evoapps.evoshift.domain.model.RosterVisibility
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.AccessLevel
import com.evoapps.evoshift.domain.model.Feature
import com.evoapps.evoshift.domain.model.can
import com.evoapps.evoshift.domain.model.Workplace
import com.evoapps.evoshift.domain.model.teamSeesWholeRoster
import com.evoapps.evoshift.domain.model.workerId
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.util.WorkplaceAccessRevokedException
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.conflate
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.emitAll
import kotlinx.coroutines.flow.emptyFlow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.mapLatest
import kotlinx.coroutines.flow.merge
import kotlinx.coroutines.flow.onStart
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.coroutines.withTimeoutOrNull
import kotlin.time.Duration
import kotlin.time.Duration.Companion.seconds
import kotlin.time.TimeMark
import kotlin.time.TimeSource

/**
 * SH-255: the shifts as the signed-in person may read them. Where a workplace limits what employees
 * see of colleagues' shifts ([RosterVisibility]) or hides someone in the roster for the others, the
 * rules let an employee read only their own and unassigned shifts directly (a month's query would
 * be refused): their roster is then their own shifts (live, offline too) and the rest of the month
 * from the server ([ShiftRepository.getTeamRoster], filtered there), read again when the roster
 * or their own shifts change and every [refreshEvery]. Managers, owners and workplaces that show the whole roster
 * read it directly, as before. Every screen, the widget and the watch go through this, so none of
 * them needs to know.
 */
class RosterPrivacyShiftRepository(
    private val inner: ShiftRepository,
    /** The signed-in person (null: nobody). */
    private val viewer: suspend () -> User?,
    /** The workplace's document (null: not readable now). */
    private val workplace: suspend (workplaceId: String) -> Workplace?,
    /** Fires when the roster month's document changes (a publish). */
    private val rosterChanges: (rosterId: String) -> Flow<Unit> = { emptyFlow() },
    /** How often an open roster reads the server's month again (null: only when it changes). */
    private val refreshEvery: Duration? = null,
    /** How long a decision about a workplace is kept before reading the setting again. */
    private val decisionFor: Duration = 60.seconds,
    /** How long the server's month is waited for (offline, a request may hang) before going without it. */
    private val teamTimeout: Duration = 10.seconds,
    private val timeSource: TimeSource = TimeSource.Monotonic
) : ShiftRepository by inner {

    /** An employee who sees part of the roster at a workplace: who, and under which setting. */
    private data class Limit(val viewerId: String, val mode: RosterVisibility)

    private data class Decision(val viewerId: String, val limit: Limit?, val at: TimeMark)

    private val mutex = Mutex()
    private val decisions = mutableMapOf<String, Decision>()
    /** The last month read from the server per roster, shown at once when a screen opens again. */
    private val lastTeam = mutableMapOf<String, List<Shift>>()

    /** Null: [workplaceId]'s whole roster is readable directly (or this cannot be told now). */
    private suspend fun limitAt(workplaceId: String, fresh: Boolean = false): Limit? {
        val user = runCatching { viewer() }.getOrNull() ?: return null
        mutex.withLock {
            val known = decisions[workplaceId]
            if (!fresh && known != null && known.viewerId == user.uid && known.at.elapsedNow() < decisionFor) return known.limit
        }
        val engagement = user.engagements[workplaceId]
        // Managers and the owner see everything (SH-270: roster view, as the rules' readsShift);
        // someone without a running engagement reads as the rules let them (their own shifts by
        // their id), as before.
        val decided = if (engagement == null || !engagement.isActive || engagement.can(Feature.ROSTER, AccessLevel.VIEW)) {
            null
        } else {
            val place = runCatching { workplace(workplaceId) }.getOrNull() ?: return null
            if (place.ownerId == user.uid || place.settings.teamSeesWholeRoster) null
            else Limit(user.uid, place.settings.rosterVisibility)
        }
        mutex.withLock { decisions[workplaceId] = Decision(user.uid, decided, timeSource.markNow()) }
        return decided
    }

    private suspend fun limitFor(rosterId: String, fresh: Boolean = false): Limit? =
        limitAt(RosterIds.workplaceIdOf(rosterId), fresh)

    /** Own shifts and the server's month, merged (own as read directly), as the person may see them. */
    private fun merged(own: List<Shift>, team: List<Shift>, limit: Limit): List<Shift> {
        val ownIds = own.mapTo(HashSet()) { it.id }
        return RosterPrivacy.visibleTo(own + team.filter { it.id !in ownIds }, limit.viewerId, limit.mode)
            .sortedBy { it.plannedTime.start }
    }

    private suspend fun teamOf(rosterId: String): List<Shift>? =
        withTimeoutOrNull(teamTimeout) { inner.getTeamRoster(rosterId).getOrNull() }
            ?.also { team -> mutex.withLock { lastTeam[rosterId] = team } }

    private suspend fun limitedMonth(rosterId: String, limit: Limit): List<Shift> = coroutineScope {
        val own = async { inner.getOwnShiftsInRoster(rosterId, limit.viewerId).getOrThrow() }
        // Offline (or the function failing): the person's own shifts, and the last month read.
        val team = async { teamOf(rosterId) ?: mutex.withLock { lastTeam[rosterId] }.orEmpty() }
        merged(own.await(), team.await(), limit)
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    private fun observeLimited(rosterId: String, limit: Limit): Flow<List<Shift>> {
        val own = inner.observeOwnShiftsInRoster(rosterId, limit.viewerId)
        val every = refreshEvery
        val ticks = if (every == null) emptyFlow() else flow {
            while (true) {
                delay(every)
                emit(Unit)
            }
        }
        val team = merge(flowOf(Unit), rosterChanges(rosterId).catch { }, ticks, own.map { })
            .conflate()
            .mapLatest { teamOf(rosterId) }
            .map { it ?: mutex.withLock { lastTeam[rosterId] }.orEmpty() }
            // A month read before shows at once; otherwise the first read (or, offline, their own
            // shifts alone once it fails or times out).
            .onStart { mutex.withLock { lastTeam[rosterId] }?.let { emit(it) } }
            .distinctUntilChanged()
        return combine(own, team) { mine, theirs -> merged(mine, theirs, limit) }.distinctUntilChanged()
    }

    override fun observeShiftsForRoster(rosterId: String): Flow<List<Shift>> = flow {
        val limit = limitFor(rosterId)
        if (limit != null) {
            emitAll(observeLimited(rosterId, limit))
            return@flow
        }
        emitAll(
            inner.observeShiftsForRoster(rosterId).catch { e ->
                // The workplace started limiting the roster while it was open: read it the limited way.
                val now = if (e is WorkplaceAccessRevokedException) limitFor(rosterId, fresh = true) else null
                if (now != null) emitAll(observeLimited(rosterId, now)) else throw e
            }
        )
    }

    override suspend fun getShiftsForRoster(rosterId: String): Result<List<Shift>> {
        limitFor(rosterId)?.let { limit -> return runCatching { limitedMonth(rosterId, limit) } }
        return inner.getShiftsForRoster(rosterId).recoverCatching { e ->
            val now = limitFor(rosterId, fresh = true) ?: throw e
            limitedMonth(rosterId, now)
        }
    }

    override fun observePendingSwaps(rosterId: String): Flow<List<Shift>> = flow {
        val limit = limitFor(rosterId)
        if (limit == null) emitAll(inner.observePendingSwaps(rosterId))
        else emitAll(observeLimited(rosterId, limit).map { shifts -> shifts.filter { it.swapRequest != null }.sortedBy { it.date } })
    }

    override suspend fun getShift(shiftId: String): Result<Shift> =
        // A colleague's shift the person may see (an offer, a day they work together) through the server.
        inner.getShift(shiftId).recoverCatching { e -> inner.getTeamShift(shiftId).getOrElse { throw e } }

    override suspend fun getUserShiftsInWorkplace(workplaceId: String, userId: String, startDate: String, endDate: String): Result<List<Shift>> {
        if (limitAt(workplaceId) == null) return inner.getUserShiftsInWorkplace(workplaceId, userId, startDate, endDate)
        return inWorkplace(workplaceId, startDate, endDate).map { shifts -> shifts.filter { it.userId == userId } }
    }

    override suspend fun getShiftsWorkedInWorkplace(workplaceId: String, userId: String, startDate: String, endDate: String): Result<List<Shift>> {
        if (limitAt(workplaceId) == null) return inner.getShiftsWorkedInWorkplace(workplaceId, userId, startDate, endDate)
        return inWorkplace(workplaceId, startDate, endDate).map { shifts -> shifts.filter { it.workerId == userId } }
    }

    private suspend fun inWorkplace(workplaceId: String, startDate: String, endDate: String): Result<List<Shift>> = runCatching {
        RosterIds.monthsBetween(startDate, endDate)
            .flatMap { (year, month) -> getShiftsForRoster(RosterIds.of(workplaceId, year, month)).getOrThrow() }
            .filter { it.date in startDate..endDate }
            .sortedBy { it.plannedTime.start }
    }
}
